import { useState } from "react";
import type { PositionRequest } from "@patent-studio/api-types";
import { Note } from "@/components/ui/Note";
import { refusalDetail } from "@/services/api-client";
import { useUnsavedDraft } from "@/hooks/use-unsaved-draft";
import type { MappableElement } from "./element-mapping";
import { EMPTY_DRAFT, PositionForm } from "./PositionForm";

const NOT_ADDED = "The position could not be added. Try again.";

/**
 * The form that adds a position, open until the server has taken it. A refusal leaves the
 * form and its words in place and says why under it; Add position sends them again.
 */
export function NewPositionForm({
  elements,
  busy,
  onAdd,
  onDone,
}: {
  elements: readonly MappableElement[];
  busy: boolean;
  onAdd: (request: PositionRequest) => Promise<unknown>;
  onDone: () => void;
}) {
  const [refusal, setRefusal] = useState<string | null>(null);
  // The form's own draft guard covers what is typed; this keeps a refused add counted too.
  useUnsavedDraft(refusal !== null);
  return (
    <>
      <PositionForm
        idPrefix="new-position"
        initial={EMPTY_DRAFT}
        elements={elements}
        submitLabel="Add position"
        busy={busy}
        onSubmit={(request) => {
          setRefusal(null);
          onAdd(request)
            .then(onDone)
            .catch((failure: unknown) => {
              setRefusal(refusalDetail(failure, NOT_ADDED));
            });
        }}
        onCancel={onDone}
      />
      {refusal !== null && (
        <Note tone="destructive" role="alert" className="mt-1">
          {refusal}
        </Note>
      )}
    </>
  );
}
